import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type {
  FpEventData,
  FpUserData,
  FpParticipationData,
  FpMeetingPointData,
  FpFriendshipData,
  FpNotificationData,
} from '../data/types';
import {
  depositRegistration,
  depositLeave,
  readRegistrationNotifications,
  materializeAttendance,
  deleteParticipation,
  countUserParticipations,
  canonicalEventId,
} from '../data/registration';
import { inbox } from '@ng-eventually/polyfill';
import type { Nuri } from '@ng-eventually/polyfill';
import {
  CURRENT_USER_ID,
  seedEvents,
  seedUsers,
  seedParticipations,
  seedMeetingPoints,
  seedFriendships,
} from '../data/seedData';
import { useNextGraph } from './NextGraphContext';
import { normalizeIdentifier } from '../utils/identifier';
// Relationship is a Festipod concept: the app keeps its own bilateral registry
// and shares its own documents' keys with its neighbours (see shared/utils/connections).
import { declareConnections } from '../utils/connections';
import {
  listMyEntityDocs,
  createEntityDoc,
  openDocumentInbox,
} from '../utils/storeRegistry';
import { useCurrentPrincipal } from '../utils/currentPrincipal';
import { createSerialTask } from '../utils/serialTask';
import { useShapeQuery } from '../data/useShapeQuery';
import { adaptEvents, adaptUsers, adaptParticipations } from '../data/shapeAdapters';
// The ORM generator emits BARE shape names (`EventShapeType`, `Event`, …), taken
// from each shape's IRI local name. They are aliased back to the `Fp*` spelling
// HERE, at the import, so nothing downstream depends on the generator's naming:
// `Event` and `Notification` are DOM globals, and the bare names collide with the
// app's own vocabulary (`FpEventData` & co. in `data/types.ts`). The prefix cannot
// be restored at the generator — the emitted name follows the shape IRI, and those
// IRIs are the persisted RDF classes (`ENTITY_TYPE`), not ours to rename.
import {
  EventShapeType as FpEventShapeType,
  UserProfileShapeType as FpUserProfileShapeType,
  ParticipationShapeType as FpParticipationShapeType,
} from '../shapes/orm/festipodShapes.shapeTypes';
import { writeEntity, updateEntityField, ENTITY_TYPE, str, int, flt, bool, iri } from '../data/entityWrites';
import { bootstrapWallet, type BootstrapResult } from '../utils/ngBootstrap';
import { autoSeedEnabled, fixtureSeedEnabled, shouldAutoSeed } from '../utils/autoSeed';

// ============================================================================
// Context interface
// ============================================================================

/**
 * Whether this session may WRITE an event's document — which, by the SDK
 * contract, is exactly whether it OWNS that document: *"Only a document's owner
 * writes to it. Holding its read key never grants a write"*, and *"No delegated
 * writing. A received key never grants a write, and no call adds a writer to a
 * document"*. So there is nothing extra to ask: owning it IS being able to write
 * it, and the owned-document listing already answers that.
 *
 * `'unknown'` is a REAL third answer, never a polite `'not-mine'`. The listing
 * may not have landed yet, or may have failed — and a rejection means UNKNOWN,
 * never "this session owns nothing". Collapsing it into `'not-mine'` is how you
 * end up telling an owner, silently and wrongly, that the thing is not theirs.
 */
export type EventOwnership = 'mine' | 'not-mine' | 'unknown';

interface FestipodDataContextValue {
  currentUserId: string;
  currentUser: FpUserData | undefined;
  /**
   * WHO THE SESSION SIGNED IN AS — the identity `ensureIdentity()` returned,
   * known before any document is read. For DISPLAY (and log attribution) only:
   * it is a different id space from `currentUserId` (a profile document NURI),
   * it is never written into an entity, and no data call takes it.
   */
  currentPrincipal: string;

  events: FpEventData[];
  users: FpUserData[];
  participations: FpParticipationData[];
  meetingPoints: FpMeetingPointData[];
  friendships: FpFriendshipData[];
  /** Host-facing notifications, surfaced from the inbox read (T02.c). */
  notifications: FpNotificationData[];

  getEvent(id: string): FpEventData | undefined;
  getUser(id: string): FpUserData | undefined;
  getEventParticipants(eventId: string): FpUserData[];
  getUserEvents(userId: string): FpEventData[];
  isParticipating(eventId: string, userId?: string): boolean;
  getFriends(userId?: string): FpUserData[];
  getEventMeetingPoints(eventId: string): FpMeetingPointData[];
  /**
   * May this session WRITE this event's document? Callers MUST treat `'unknown'`
   * as its own case — see {@link EventOwnership}.
   */
  getEventOwnership(eventId: string): EventOwnership;

  selectedEventId: string;
  setSelectedEventId(id: string): void;
  selectedEvent: FpEventData | undefined;
  selectedUserId: string;
  setSelectedUserId(id: string): void;
  selectedUser: FpUserData | undefined;

  createEvent(event: Omit<FpEventData, 'id'>): Promise<FpEventData>;
  updateEvent(id: string, updates: Partial<FpEventData>): void | Promise<void>;
  joinEvent(eventId: string, userId?: string): Promise<void> | void;
  leaveEvent(eventId: string, userId?: string): Promise<void> | void;
  addMeetingPoint(mp: Omit<FpMeetingPointData, 'id'>): void;
  addFriend(friendId: string): void;
  updateProfile(updates: Partial<FpUserData>): void | Promise<void>;
  loadTestData(): Promise<BootstrapResult>;
}

const FestipodDataContext = createContext<FestipodDataContextValue | null>(null);

// ============================================================================
// Helpers
// ============================================================================

let idCounter = 100;
function nextId(prefix: string): string {
  return `${prefix}-${++idCounter}`;
}

// LEGACY, READ ONLY. Participations written by an older version key their user
// (`fp:user`) as `urn:festipod:user:<key>` instead of the UserProfile's `did:ng:`
// document NURI. Nothing MINTS this any more — today's writes carry the profile
// NURI (`currentUserId`) — but `resolveParticipantUser` must still recognize the
// old form, or those participations render as "participant inconnu".
const USER_PRINCIPAL_PREFIX = 'urn:festipod:user:';

/** Waits between attempts at resolving an owned-document set (see the effects). */
const OWNED_RETRY_BACKOFF_MS = [500, 1500, 4000];

/**
 * What a BRAND-NEW profile is created with.
 *
 * A profile is entirely Festipod's own object — the data layer knows nothing of
 * pseudos or display names — but the UserProfile SHEX shape makes `fp:name`,
 * `fp:initials` and `fp:username` MANDATORY, so a profile cannot be written
 * empty. And nothing about the person is known at sign-in: the identity the
 * session signed in as is OPAQUE (no display name; it is never parsed, never
 * rendered, never written into an entity). So the three required fields carry a
 * PLACEHOLDER that reads on screen as "not filled in yet": none of them is a
 * person's name or handle, and none is derived from the identity. The user
 * replaces them through `updateProfile` (UpdateProfileScreen).
 */
const UNSET_PROFILE = {
  name: 'Profil à compléter',
  initials: '?',
  username: '(pseudo non défini)',
} as const;

/**
 * Resolve a Participation's `fp:user` to its UserProfile.
 *
 * TODAY'S WRITES need no resolving: `fp:user` carries the profile's own document
 * NURI, so the DIRECT match `u.id === userId` answers — and it is tried first.
 * The demo/local fixtures coincide there too (both sides are the bare `user-1`).
 *
 * LEGACY ONLY: participations written under the earlier scheme carry
 * `urn:festipod:user:<normalized-handle>`, which matches no profile id. For those
 * — and ONLY those — the handle is stripped off and compared to
 * `normalizeIdentifier(profile.username)`. This username bridge is a READ-side
 * survival for old data; it plays NO part in deciding who the current user is
 * (my profile is the profile document I own — see the "WHO AM I" block below).
 *
 * A per-deposit materializer uid (`mint...`, e.g. `mrktnoke-rzd699dk`) is a third,
 * unrelated space: it identifies an inbox deposit for the count, never a user.
 */
function resolveParticipantUser(userId: string, users: FpUserData[]): FpUserData | undefined {
  // 1) Direct id match — demo/local seed space (`user-1`), or any coincident space.
  const direct = users.find(u => u.id === userId);
  if (direct) return direct;
  // 2) NG principal space: `urn:festipod:user:<key>` → match on normalized username.
  const key = userId.startsWith(USER_PRINCIPAL_PREFIX)
    ? userId.slice(USER_PRINCIPAL_PREFIX.length)
    : userId;
  return users.find(u => u.username && normalizeIdentifier(u.username) === key);
}

// NG shape → app type mapping lives in `../data/shapeAdapters` (domain adapters
// over the SDK's `watchShape` subjects).

// ============================================================================
// Shared queries builder — same logic for both local and NG modes
// ============================================================================

function buildQueries(
  events: FpEventData[],
  users: FpUserData[],
  participations: FpParticipationData[],
  meetingPoints: FpMeetingPointData[],
  friendships: FpFriendshipData[],
  currentUserId: string,
) {
  const getEvent = (id: string) => events.find(e => e.id === id);
  const getUser = (id: string) => users.find(u => u.id === id);

  const getEventParticipants = (eventId: string) => {
    // Resolve each of the event's participations to its UserProfile across the two
    // id spaces (participation principal vs profile NURI) — see
    // `resolveParticipantUser`. A raw `partUserIds.includes(u.id)` join never
    // matched in connected mode (principal ≠ NURI) → every participant rendered as
    // "unknown". Deduped, returned in `users` order to match the prior contract.
    const eventParts = participations.filter(p => p.eventId === eventId);
    const resolvedIds = new Set<string>();
    for (const p of eventParts) {
      const u = resolveParticipantUser(p.userId, users);
      if (u) resolvedIds.add(u.id);
    }
    return users.filter(u => resolvedIds.has(u.id));
  };

  const getUserEvents = (userId: string) => {
    const partEventIds = participations.filter(p => p.userId === userId).map(p => p.eventId);
    return events.filter(e => partEventIds.includes(e.id));
  };

  const isParticipating = (eventId: string, userId?: string) => {
    const uid = userId || currentUserId;
    return participations.some(p => p.eventId === eventId && p.userId === uid);
  };

  const getFriends = (userId?: string) => {
    const uid = userId || currentUserId;
    const friendIds = friendships
      .filter(f => f.userId === uid || f.friendId === uid)
      .map(f => f.userId === uid ? f.friendId : f.userId);
    return users.filter(u => friendIds.includes(u.id));
  };

  const getEventMeetingPoints = (eventId: string) => {
    return meetingPoints.filter(mp => mp.eventId === eventId);
  };

  return { getEvent, getUser, getEventParticipants, getUserEvents, isParticipating, getFriends, getEventMeetingPoints };
}

// ============================================================================
// Local (disconnected) provider — uses seed data directly, read-only
// ============================================================================

function useLocalData(empty?: boolean): FestipodDataContextValue {
  const [selectedEventId, setSelectedEventId] = useState<string>(empty ? '' : 'event-1');
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const events = empty ? [] : seedEvents;
  const users = empty ? [] : seedUsers;
  const participations = empty ? [] : seedParticipations;
  const meetingPoints = empty ? [] : seedMeetingPoints;
  const friendships = empty ? [] : seedFriendships;

  // Demo mode has a single fixture user — the app names no identity of its own.
  const currentUserId = empty ? '' : CURRENT_USER_ID;
  // Who signed in, when the barrier has settled. Demo mode reads no document, so
  // this is the only real identity it can attribute a log line to.
  const currentPrincipal = useCurrentPrincipal();
  // Identity-first log prefix: the fixture user when there is one, else the
  // signed-in principal, else the bare `[app][data]` form.
  const logPrefix = currentUserId || currentPrincipal
    ? `[${currentUserId || currentPrincipal}][app][data]`
    : '[app][data]';
  const currentUser = users.find(u => u.id === currentUserId);
  const selectedEvent = events.find(e => e.id === selectedEventId);
  const selectedUser = users.find(u => u.id === selectedUserId);

  const queries = buildQueries(events, users, participations, meetingPoints, friendships, currentUserId);

  console.log(`${logPrefix} Render —`, empty ? 'connecting (empty)' : 'local',
    '| events:', events.length,
    '| selectedEvent:', selectedEvent?.title ?? '(none)');

  // Local mode: mutations are no-ops (static defaults)
  const createEvent = useCallback(async (event: Omit<FpEventData, 'id'>): Promise<FpEventData> => {
    console.log(`${logPrefix} createEvent (local, no-op):`, event.title);
    return { ...event, id: nextId('event') };
  }, [logPrefix]);
  const updateEvent = useCallback((_id: string, _updates: Partial<FpEventData>) => {
    console.log(`${logPrefix} updateEvent (local, no-op)`);
  }, [logPrefix]);
  const joinEvent = useCallback((_eventId: string) => {
    console.log(`${logPrefix} joinEvent (local, no-op)`);
  }, [logPrefix]);
  const leaveEvent = useCallback((_eventId: string) => {
    console.log(`${logPrefix} leaveEvent (local, no-op)`);
  }, [logPrefix]);
  const addMeetingPoint = useCallback((_mp: Omit<FpMeetingPointData, 'id'>) => {
    console.log(`${logPrefix} addMeetingPoint (local, no-op)`);
  }, [logPrefix]);
  const addFriend = useCallback((_friendId: string) => {
    console.log(`${logPrefix} addFriend (local, no-op)`);
  }, [logPrefix]);
  const updateProfile = useCallback((_updates: Partial<FpUserData>) => {
    console.log(`${logPrefix} updateProfile (local, no-op)`);
  }, [logPrefix]);
  const loadTestData = useCallback(async (): Promise<BootstrapResult> => {
    console.log(`${logPrefix} loadTestData (local, no-op)`);
    return { seeded: false, userIdMap: new Map(), eventIdMap: new Map(), createdDocs: { public: [], protected: [] } };
  }, []);
  // Demo mode owns its whole fixture world. There is no document, no wallet and
  // no writing here — every mutation above is a local no-op — so there is no
  // ownership to look up and no listing that could be pending: the answer is
  // settled, and it is the same one demo mode gives everywhere else, which is
  // "yes, the app is yours to drive". `'unknown'` would leave the affordance
  // pending forever on a path where nothing is ever going to resolve.
  const getEventOwnership = useCallback((): EventOwnership => 'mine', []);

  return {
    currentUserId, currentUser, currentPrincipal,
    events, users, participations, meetingPoints, friendships,
    notifications: [],
    selectedEventId, setSelectedEventId, selectedEvent,
    selectedUserId, setSelectedUserId, selectedUser,
    ...queries,
    getEventOwnership,
    createEvent, updateEvent, joinEvent, leaveEvent,
    addMeetingPoint, addFriend, updateProfile, loadTestData,
  };
}

// ============================================================================
// NextGraph-connected provider — reads directly from NG wallet
// ============================================================================

function useNgData(): FestipodDataContextValue {
  const { session } = useNextGraph();
  // The app speaks ONLY in logical scopes — it holds no store id and builds no
  // `did:ng:${…}` NURI. It creates ONE document PER ENTITY in its scope
  // (`createEntityDoc(scope)`, the SDK create) and READS via the SDK's reactive,
  // `useQuery`-shaped surface `watchShape(shape, scope)` (bound to React by
  // `useShapeQuery`). The observable resolves the scope to the current identity's
  // wallet (its own scope docs + discovery for public), awaits the sync barrier,
  // and pushes on every change — no bespoke re-query, no manual doc listing, no
  // per-doc subscription in the app. See rule_app-uses-sdk-surface-only.
  const ready = !!session;

  // --- REACTIVE READS via the SDK surface (`watchShape` bound with useShapeQuery) --
  // Three scoped shape reads, mapped to the app's domain types. Each is reactive
  // (broker push, no polling): a locally-created entity, a seeded doc, or a remote
  // peer's public event all re-render through the observable's own subscriptions.
  //   • events         = public  (my own public event docs + discovery index)
  //   • profiles/users = protected (my own)
  //   • participations = protected (my own; cap-filtered by the SDK)
  const eventQuery = useShapeQuery(FpEventShapeType, 'public');
  const userQuery = useShapeQuery(FpUserProfileShapeType, 'protected');
  const partQuery = useShapeQuery(FpParticipationShapeType, 'protected');
  // The RAW reactive sets straight from `watchShape` (before the optimistic
  // overlay). The exposed `events`/`users`/`participations` merge these with the
  // pending overlay below — see the "OPTIMISTIC OVERLAY" block.
  const reactiveUsers = React.useMemo(() => adaptUsers(userQuery.data), [userQuery.data]);
  const reactiveEvents = React.useMemo(() => adaptEvents(eventQuery.data), [eventQuery.data]);
  const reactiveParticipations = React.useMemo(
    () => adaptParticipations(partQuery.data),
    [partQuery.data],
  );

  // --- OPTIMISTIC OVERLAY (standard useQuery-mutation pattern; NO polling) ------
  // `watchShape` only surfaces a doc AFTER the broker push (a real latency), so a
  // mutation's effect is invisible in the reactive set for a beat. To keep the
  // screens' local-first feel, each mutation reflects its effect IMMEDIATELY in a
  // pure app-side React overlay laid OVER the SDK surface (never touching the SDK
  // internals — see rule_app-uses-sdk-surface-only), then RECONCILED by the push:
  //   • `pendingAddEvents` / `pendingAddParticipations` — entities created
  //     optimistically, keyed by their `@id` (= the doc NURI `writeEntity` returns,
  //     the SAME id `watchShape` surfaces as `s.subject` → they match exactly).
  //   • `pendingRemoveIds` — participation ids removed optimistically (leave).
  // The exposed set = merge(reactive, pendingAdds) minus pendingRemoves, DEDUPED by
  // id. Reconciliation (effects below) auto-cleans the overlay the moment the
  // reactive read catches up: an add whose id now appears in the reactive set is
  // dropped from pendingAdds; a remove whose id is no longer in the reactive set is
  // dropped from pendingRemoves. No re-query, no interval — the overlay only reacts
  // to `watchShape`'s own pushes.
  const [pendingAddEvents, setPendingAddEvents] = useState<FpEventData[]>([]);
  const [pendingAddUsers, setPendingAddUsers] = useState<FpUserData[]>([]);
  const [pendingAddParticipations, setPendingAddParticipations] = useState<FpParticipationData[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<string>>(() => new Set());

  const users = React.useMemo(() => {
    if (pendingAddUsers.length === 0) return reactiveUsers;
    const seen = new Set(reactiveUsers.map(u => u.id));
    const extra = pendingAddUsers.filter(u => !seen.has(u.id));
    return extra.length ? [...reactiveUsers, ...extra] : reactiveUsers;
  }, [reactiveUsers, pendingAddUsers]);

  const events = React.useMemo(() => {
    if (pendingAddEvents.length === 0) return reactiveEvents;
    const seen = new Set(reactiveEvents.map(e => e.id));
    const extra = pendingAddEvents.filter(e => !seen.has(e.id));
    return extra.length ? [...reactiveEvents, ...extra] : reactiveEvents;
  }, [reactiveEvents, pendingAddEvents]);

  const participations = React.useMemo(() => {
    let base = reactiveParticipations;
    if (pendingAddParticipations.length) {
      const seen = new Set(base.map(p => p.id));
      const extra = pendingAddParticipations.filter(p => !seen.has(p.id));
      if (extra.length) base = [...base, ...extra];
    }
    if (pendingRemoveIds.size) base = base.filter(p => !pendingRemoveIds.has(p.id));
    return base;
  }, [reactiveParticipations, pendingAddParticipations, pendingRemoveIds]);

  // RECONCILIATION — drop each optimistic add once the reactive read carries its id
  // (the push arrived), and each optimistic remove once the reactive read no longer
  // carries its id (the SPARQL delete propagated). Guarded to no-op when there is
  // nothing to reconcile, so a stable reactive set does not churn state.
  useEffect(() => {
    if (pendingAddEvents.length === 0) return;
    const live = new Set(reactiveEvents.map(e => e.id));
    setPendingAddEvents(prev => {
      const next = prev.filter(e => !live.has(e.id));
      return next.length === prev.length ? prev : next;
    });
  }, [reactiveEvents, pendingAddEvents]);

  useEffect(() => {
    if (pendingAddUsers.length === 0) return;
    const live = new Set(reactiveUsers.map(u => u.id));
    setPendingAddUsers(prev => {
      const next = prev.filter(u => !live.has(u.id));
      return next.length === prev.length ? prev : next;
    });
  }, [reactiveUsers, pendingAddUsers]);

  useEffect(() => {
    if (pendingAddParticipations.length === 0) return;
    const live = new Set(reactiveParticipations.map(p => p.id));
    setPendingAddParticipations(prev => {
      const next = prev.filter(p => !live.has(p.id));
      return next.length === prev.length ? prev : next;
    });
  }, [reactiveParticipations, pendingAddParticipations]);

  useEffect(() => {
    if (pendingRemoveIds.size === 0) return;
    const live = new Set(reactiveParticipations.map(p => p.id));
    setPendingRemoveIds(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) if (!live.has(id)) { next.delete(id); changed = true; }
      return changed ? next : prev;
    });
  }, [reactiveParticipations, pendingRemoveIds]);
  // The read is "settled" once every scope has reached its sync barrier
  // (`isSuccess`). A synced-but-empty scope reads `isSuccess` with `data: []` — the
  // distinction the auto-seed relies on to tell "still syncing" from "truly empty".
  const readReady =
    eventQuery.isSuccess && userQuery.isSuccess && partQuery.isSuccess;

  // Session-local map `${eventId}|${userId}` → the join deposit's uid, so a leave
  // in the SAME session can carry `regUid` for a precise cancellation. Absent it
  // (cross-session leave), the owner's materializer falls back to (event, user)
  // matching — so this is an optimization, not a correctness dependency.
  //
  // Nothing ever clears this map, and nothing needs to: a browser context is one
  // user for its whole life (`ensureIdentity()` answers once, before anything
  // renders), so there is never anyone else's leftovers in it.
  const joinUidsRef = useRef<Map<string, string>>(new Map());

  // OPTION B — the set of event docs the CURRENT identity OWNS (its own public
  // event docs). Each such NURI IS the event `@id` (writeEntity uses the doc NURI
  // as the subject). The owner-materializer subscribes to each owned event's inbox
  // and writes `participantCount` on THAT (owned) doc — never on someone else's.
  const [ownedEventIds, setOwnedEventIds] = useState<Nuri[]>([]);
  // Events the listing has RULED OUT — canonical ids that were already visible in
  // the reactive read when a listing RESOLVED, and were absent from its result.
  // ONLY these may be shown as "not yours".
  //
  // Why a set and not a "the listing answered" boolean: a listing is a SNAPSHOT,
  // and it can only ever UNDER-report ownership, because a document created after
  // it was taken cannot possibly be in it. So a miss is authoritative for what
  // existed at the time and says NOTHING about anything that arrived since. With a
  // boolean, a second tab on the same wallet that creates an event has it pushed
  // here, misses the stale snapshot, and its owner is told the event is not theirs
  // — permanently. That is the exact failure this whole answer exists to avoid.
  const [ruledOutEventIds, setRuledOutEventIds] = useState<ReadonlySet<string>>(new Set());
  // The owned set in canonical id-form — the form every event-id comparison here
  // uses, so an event reached under a different overlay is still the same event.
  const ownedCanonicalIds = React.useMemo(
    () => new Set(ownedEventIds.map(canonicalEventId)),
    [ownedEventIds],
  );

  // WHICH PROTECTED DOCUMENTS ARE MINE — the ground on which "my profile" rests.
  // `null` means NOT ANSWERED YET (the listing has not landed, or it failed):
  // "unknown" and "I own nothing" are indistinguishable as an empty array, and
  // only one of them may lead to creating a profile. Nothing downstream may read
  // `null` as an empty set.
  const [myProtectedDocs, setMyProtectedDocs] = useState<Nuri[] | null>(null);
  // The profile document THIS session created for the signed-in person. Set once,
  // by the creation effect; it settles "which of my profile documents is mine"
  // without looking at any field of any profile.
  const [myProfileDocId, setMyProfileDocId] = useState<string>('');

  /**
   * Fold documents THIS SESSION just created into the owned set.
   *
   * The backfill below runs once, on connection, so it only ever knows what
   * existed at mount. Everything created afterwards has to announce itself, or it
   * is owned in fact and unowned as far as this tree is concerned — and the
   * owner-materializer, which only ever looks at this set, never runs on it.
   */
  const claimOwnedEventDocs = useCallback((created: Nuri[]) => {
    if (created.length === 0) return;
    setOwnedEventIds(prev => [...new Set([...prev, ...created])]);
  }, []);

  // Resolve the CURRENT identity's owned public event docs for the materializer
  // ONLY (decoupled from the read — `watchShape` resolves reads itself). Bounded to
  // this session's own documents (`listMyEntityDocs('public')` — "mine" needs no
  // identity, the session is one user's). Runs on connection to backfill events
  // owned before this mount; `createEvent` and the seed (`loadTestData` /
  // auto-seed) claim what they create through `claimOwnedEventDocs`. This is NOT a
  // read path (it feeds no `events`/`users`/`participations`), only the
  // owner-count derivation.
  //
  // A REJECTION HERE MEANS "UNKNOWN", NEVER "THIS SESSION OWNS NOTHING". The two
  // are indistinguishable downstream — both leave the set empty — but only one of
  // them is true, and taking the wrong one silently disables the owner
  // materializer, so every event this session hosts stops converging. So the
  // failure is RETRIED, and if it still will not answer, it is said loudly instead
  // of leaving a plausible-looking empty set behind.
  // Read through refs inside the async listing below, so classification uses what
  // is on screen when the listing RESOLVES rather than what was there when it
  // started — the two differ by exactly the push that prompted a re-listing.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const ownedCanonicalIdsRef = useRef(ownedCanonicalIds);
  ownedCanonicalIdsRef.current = ownedCanonicalIds;
  // Events the snapshot cannot speak about: neither owned nor ruled out. While
  // this is non-empty the listing is re-taken — ONCE per new arrival, never on a
  // timer ([[rule_no-broker-polling]]): a listing classifies everything visible at
  // the moment it resolves, so this empties and the effect falls silent until a
  // genuinely new event turns up.
  const unclassifiedEventKey = React.useMemo(() => {
    const pending = events
      .map(e => canonicalEventId(e.id))
      .filter(c => !ownedCanonicalIds.has(c) && !ruledOutEventIds.has(c));
    return [...new Set(pending)].sort().join('|');
  }, [events, ownedCanonicalIds, ruledOutEventIds]);
  const listingTaken = useRef(false);
  useEffect(() => {
    if (!ready) return;
    // Nothing left to learn: a listing has been taken and every visible event is
    // classified. Without this the effect would re-list on its own state change.
    if (listingTaken.current && unclassifiedEventKey === '') return;
    let cancelled = false;
    (async () => {
      for (let attempt = 0; !cancelled; attempt++) {
        try {
          const myPublic = await listMyEntityDocs('public');
          if (cancelled) return;
          setOwnedEventIds(prev => [...new Set([...prev, ...myPublic])]);
          listingTaken.current = true;
          // The listing ANSWERED — so every event visible RIGHT NOW that it did
          // not return is genuinely not this session's, and may be shown as such.
          // Anything arriving after this point stays unclassified and triggers a
          // fresh listing rather than inheriting this one's silence. Events already
          // claimed as mine (created here, possibly racing this listing) are never
          // ruled out.
          // REBUILT, never accumulated: every listing RE-ADJUDICATES everything
          // visible, so a later listing can overturn an earlier ruling instead of
          // being outvoted by it.
          //
          // KNOWN RESIDUAL, and it needs the provider — not more code here. This
          // infers "not yours" from ABSENCE, and absence is not authoritative: the
          // reactive read and this listing are two different mechanisms, so an
          // event can be on screen a moment before the listing can see it. Ruled
          // out in that window, it is only re-examined if some OTHER unclassified
          // event later triggers a listing. Self-healing would need either a timer
          // (polling — forbidden) or the "may I write this?" call the surface does
          // not publish. Per `rule_app-uses-sdk-surface-only` that is a GAP to
          // raise, not one to paper over here, so it is left visible and stated.
          const ownedNow = new Set(myPublic.map(canonicalEventId));
          setRuledOutEventIds(() => {
            const next = new Set<string>();
            for (const e of eventsRef.current) {
              const c = canonicalEventId(e.id);
              if (!ownedNow.has(c) && !ownedCanonicalIdsRef.current.has(c)) next.add(c);
            }
            return next;
          });
          return;
        } catch (err) {
          const wait = OWNED_RETRY_BACKOFF_MS[attempt];
          if (wait === undefined) {
            console.error(
              `${logPrefix} owned-events resolution FAILED after ${OWNED_RETRY_BACKOFF_MS.length + 1} ` +
                `attempts — the owned set is UNKNOWN, not empty, and events hosted by this session ` +
                `will not converge until it is known:`,
              err,
            );
            return;
          }
          console.warn(
            `${logPrefix} owned-events resolution failed (attempt ${attempt + 1}) — retrying in ${wait}ms:`,
            err,
          );
          await new Promise(r => setTimeout(r, wait));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ready, unclassifiedEventKey]);

  // Not in SHEX shapes yet
  const [meetingPoints, setMeetingPoints] = useState<FpMeetingPointData[]>([]);
  const [friendships, setFriendships] = useState<FpFriendshipData[]>([]);
  // Host-facing notifications, materialized from the current user's inboxes
  // (the inbox read, T02.b/c). Data-level surfacing of "new participants".
  const [notifications, setNotifications] = useState<FpNotificationData[]>([]);

  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Auto-select first event when data appears from NG
  useEffect(() => {
    const first = events[0];
    if (!selectedEventId && first) {
      setSelectedEventId(first.id);
    }
  }, [events.length, selectedEventId]);

  // Auto-seed: bootstrap seed data into a genuinely EMPTY wallet. Gated on the
  // SDK's `isSuccess` (readReady) — the sync barrier is reached for every scope —
  // so an empty set means "synced and truly empty", NOT "still syncing". This
  // replaces the old 3s chronometer heuristic (which guessed a sync delay and mis-
  // fired a re-seed on the 3rd connect). `isPending` → wait; `isSuccess` + empty
  // data → seed. Gated behind the `FESTIPOD_AUTO_SEED` env var (see utils/autoSeed):
  // OFF BY DEFAULT so a fresh/persistent wallet is never silently bloated with demo
  // data — the seed only runs when explicitly opted in. Explicit `loadTestData`
  // (the test-data action + every @data test) is UNAFFECTED (it seeds directly).
  // `hasTriedAutoSeed` keeps it single-shot (also suppressed by an explicit
  // `loadTestData`).
  //
  // ALL OF THAT IS CURRENTLY MOOT: the fixture seed is switched OFF outright
  // (`fixtureSeedEnabled`), so this effect returns on its first line and no
  // fixture reaches the wallet. `bootstrapWallet` enforces the same switch, so
  // the check here buys only silence — no misleading "bootstrapping…" log, and
  // no work started that would write nothing.
  const hasTriedAutoSeed = useRef(false);
  useEffect(() => {
    if (!fixtureSeedEnabled()) return;
    if (!autoSeedEnabled()) return;
    if (hasTriedAutoSeed.current) return;
    if (!ready) return;
    if (!readReady) return; // still syncing — do NOT mistake pending for empty
    // MY OWN PROFILE IS NOT "DATA IN THE WALLET". It is created at sign-in on a
    // brand-new wallet, so counting it here would permanently disable the seed on
    // exactly the wallets it exists for. The question the gate asks is "does this
    // wallet already hold something worth preserving", and my own empty profile
    // does not.
    const walletHasData = events.length > 0 || users.some(u => u.id !== myProfileDocId);
    if (!shouldAutoSeed(walletHasData)) {
      console.log(`${logPrefix} Auto-seed (FESTIPOD_AUTO_SEED): wallet already has data — skip`);
      return;
    }
    // Enabled AND synced-empty → a real empty wallet. Seed once.
    hasTriedAutoSeed.current = true;
    console.log(`${logPrefix} Auto-seed (FESTIPOD_AUTO_SEED): wallet empty (synced), bootstrapping…`);
    bootstrapWallet(false, createEntityDoc)
      // The seed's public event docs were created BY THIS SESSION — they are mine.
      .then(result => claimOwnedEventDocs(result.createdDocs.public))
      .catch(err => console.error(`${logPrefix} Auto-seed failed:`, err));
    // The reactive `watchShape` reads pick the seeded per-entity docs up on their
    // own (each createEntityDoc appends to the scope index → the container-index
    // subscription re-resolves → the new docs enter the read). No registerDoc/relist.
  }, [ready, readReady, events.length, users, myProfileDocId, claimOwnedEventDocs]);

  // --- Derived ---
  // WHO AM I — answered in TWO id spaces that must not be confused.
  //
  // (1) `currentPrincipal` — what signing in returned. Known as soon as the one
  //     identity await settles, i.e. before any document has been read. It names
  //     a PERSON, OPAQUELY: it is never parsed, never rendered as a name, never
  //     written into an entity and never handed to a data call. It exists here
  //     for display of "am I signed in" and for log attribution.
  // (2) `currentUserId` — the app's own entity space: the `@id` of MY PROFILE
  //     DOCUMENT (a doc NURI). This is what a Participation's `fp:user` carries
  //     and what `resolveParticipantUser` matches directly, so it is the only
  //     value a mutation may write.
  //
  // THERE IS NO JOIN BETWEEN THE TWO, and there must not be one. A profile is
  // Festipod's own object; the identity says nothing about it. **My profile is
  // the profile document I OWN** — `listMyEntityDocs('protected')` answers "which
  // documents are mine", and the UserProfile among them is mine. No field of any
  // profile takes part in the answer: no username comparison, no normalization,
  // no positional pick.
  //
  // THREE OUTCOMES, and "somebody else's profile" is not one of them:
  //   • exactly one owned profile → that is me;
  //   • none → I have no profile yet, and the creation effect below makes one
  //     (until it lands, `currentUserId` is '' and the mutations that need it
  //     REJECT — they never write an entity keyed on nobody);
  //   • several, none of them created by this session → genuinely AMBIGUOUS (the
  //     opt-in fixture seed writes its profiles into my own protected scope), so
  //     the answer is NO PROFILE, said loudly. Picking one would be picking a
  //     person at random and calling them "you".
  const currentPrincipal = useCurrentPrincipal();
  /** The profiles I own: the reactive profiles whose document is one of mine. */
  const myOwnedProfiles = React.useMemo<FpUserData[] | null>(() => {
    if (myProtectedDocs === null) return null; // UNKNOWN — never "none"
    const mine = new Set<string>(myProtectedDocs);
    return users.filter(u => mine.has(u.id));
  }, [users, myProtectedDocs]);
  const currentUser = React.useMemo<FpUserData | undefined>(() => {
    // This session created it → no ambiguity possible, whatever else is owned.
    if (myProfileDocId) return users.find(u => u.id === myProfileDocId);
    if (myOwnedProfiles === null) return undefined;       // not answered yet
    if (myOwnedProfiles.length === 1) return myOwnedProfiles[0];
    if (myOwnedProfiles.length === 0) return undefined;   // none — one gets created
    // SEVERAL profile documents are mine, and none was created by this session:
    // the fixture seed writes its profiles into my own protected scope, so a
    // reloaded demo wallet lands here. Pick the first by document reference —
    // stable across reloads, and arbitrary, which is honest: while the profile
    // is not a built feature, "which of my fixtures am I" has no true answer.
    //
    // This is NOT the impersonation that was removed. That one reached for a
    // profile by NAME and could land on a document belonging to somebody else.
    // Every candidate here is a document I own, so the invariant that holds is
    // the one that matters: the app never presents another person's profile as
    // mine. Delete this branch the day a profile is really created and known.
    return [...myOwnedProfiles].sort((a, b) => a.id.localeCompare(b.id))[0];
  }, [users, myProfileDocId, myOwnedProfiles]);
  const currentUserId = currentUser?.id || '';
  // Identity-first log prefix, reused by every DATA log below (including the
  // closures defined earlier in this function body — they only execute after
  // this render has finished, by which point `logPrefix` is initialized). Several
  // browser contexts can be tailed at once, so a line must say WHOSE it is: the
  // profile NURI once it is read, and the signed-in principal before that — which
  // is now from the very first render, instead of the anonymous `[app][data]`.
  const logPrefix = currentUserId || currentPrincipal
    ? `[${currentUserId || currentPrincipal}][app][data]`
    : '[app][data]';
  const selectedEvent = events.find(e => e.id === selectedEventId);
  // DISPLAY READ — log participantCount exactly as currently exposed for
  // rendering. Compared against the owner-materializer's WRITE logs below, this
  // pinpoints whether a stuck counter is a DATA problem (never incremented) or a
  // DISPLAY/read problem (incremented but not re-read until the next session).
  if (selectedEvent) {
    console.log(
      `${logPrefix} participation read for display — event=${canonicalEventId(selectedEvent.id)} ` +
        `"${selectedEvent.title}": participantCount lu = ${selectedEvent.participantCount}`,
    );
  }
  const selectedUser = users.find(u => u.id === selectedUserId);

  // --- MY PROFILE: which documents are mine ----------------------------------
  // Resolve the PROTECTED documents this identity owns. That set is the whole
  // basis of "which profile is mine", and it is also what decides whether a
  // profile has to be CREATED — so a failure here must never look like an answer:
  // a rejection means UNKNOWN, and reading it as "I own nothing" would create a
  // second profile for someone who already has one. Retried; if it still will not
  // answer, the set stays `null` (no profile resolved, no profile created) and it
  // is said loudly.
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    (async () => {
      for (let attempt = 0; !cancelled; attempt++) {
        try {
          const mine = await listMyEntityDocs('protected');
          if (cancelled) return;
          setMyProtectedDocs(prev => [...new Set([...(prev ?? []), ...mine])]);
          return;
        } catch (err) {
          const wait = OWNED_RETRY_BACKOFF_MS[attempt];
          if (wait === undefined) {
            console.error(
              `${logPrefix} my-protected-documents resolution FAILED after ` +
                `${OWNED_RETRY_BACKOFF_MS.length + 1} attempts — which profile is mine is UNKNOWN, ` +
                `not absent: no profile will be resolved and none will be created until it is known:`,
              err,
            );
            return;
          }
          console.warn(
            `${logPrefix} my-protected-documents resolution failed (attempt ${attempt + 1}) — ` +
              `retrying in ${wait}ms:`,
            err,
          );
          await new Promise(r => setTimeout(r, wait));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ready]);

  // --- MY PROFILE: create one when I have none -------------------------------
  // A profile is Festipod's own object, and signing in produces none — so the
  // first time a person signs in, the app makes theirs. Gated on BOTH the
  // protected read having settled (`userQuery.isSuccess`: synced-and-empty, not
  // still-syncing) and the owned-document set being KNOWN, because "I have no
  // profile" is only true when both have answered. Single-shot per session; on
  // failure the guard is released, so a later change to the owned set retries.
  const hasTriedProfileCreate = useRef(false);
  useEffect(() => {
    if (!ready) return;
    if (hasTriedProfileCreate.current) return;
    if (!userQuery.isSuccess) return;
    if (myOwnedProfiles === null) return;   // UNKNOWN — never read as "none"
    if (myOwnedProfiles.length > 0) return; // I already have one (or more)
    hasTriedProfileCreate.current = true;
    (async () => {
      console.log(`${logPrefix} no profile of mine — creating one (fields left visibly unset)`);
      const graph = await createEntityDoc('protected');
      // The three fields the UserProfile shape makes mandatory, written with the
      // "not filled in yet" placeholders — nothing here comes from the identity.
      await writeEntity(graph, ENTITY_TYPE.user, {
        name: str(UNSET_PROFILE.name),
        initials: str(UNSET_PROFILE.initials),
        username: str(UNSET_PROFILE.username),
      });
      // This document is MINE — claimed explicitly, so it is recognized as my
      // profile whatever else the protected scope holds (fixtures included).
      setMyProfileDocId(graph);
      setMyProtectedDocs(prev => [...new Set([...(prev ?? []), graph])]);
      // OPTIMISTIC OVERLAY, same pattern as events/participations: surface the
      // profile immediately so `currentUserId` resolves without waiting for the
      // broker push; the reconciliation effect drops it once the read carries it.
      const optimisticProfile: FpUserData = { id: graph, ...UNSET_PROFILE };
      setPendingAddUsers(prev => (prev.some(u => u.id === graph) ? prev : [...prev, optimisticProfile]));
    })().catch(err => {
      hasTriedProfileCreate.current = false;
      console.error(`${logPrefix} creating my profile FAILED — this session has no profile:`, err);
    });
  }, [ready, userQuery.isSuccess, myOwnedProfiles]);

  // --- MY PROFILE: say it when the answer is arbitrary ------------------------
  // Several profile documents are mine and none was created by this session (the
  // opt-in fixture seed writes its profiles into my own protected scope). One is
  // picked deterministically so the app stays usable on a demo wallet, but the
  // pick carries no meaning — say so once, or a fixture person silently becomes
  // "you" and nobody wonders why.
  const warnedAmbiguousProfile = useRef(false);
  useEffect(() => {
    if (myProfileDocId) return;
    if (myOwnedProfiles === null || myOwnedProfiles.length <= 1) return;
    if (warnedAmbiguousProfile.current) return;
    warnedAmbiguousProfile.current = true;
    console.warn(
      `${logPrefix} ${myOwnedProfiles.length} profile documents are mine and none was created by ` +
        `this session (a fixture seed run on this wallet is the usual cause). The first by document ` +
        `reference is used as mine — a stable but ARBITRARY pick, so the name shown as yours is ` +
        `demo data, not you.`,
    );
  }, [myOwnedProfiles, myProfileDocId]);

  // --- OWNER MATERIALIZER (Option B, brief §B.2 + T02.c notifications) -------
  // The event OWNER's session materializes its OWN events' inbox deposits into
  //   (1) the correct `participantCount` on its OWN event doc, and
  //   (2) host-facing "new participant" FpNotifications.
  // This is what makes the count CORRECT and reactive WITHOUT any non-owner ever
  // writing the event doc: the joiner only deposits; the owner counts.
  //
  // Reactive, no polling: subscribe the inbox document via `inbox.watch` (a
  // `doc_subscribe` push). One watch PER owned event: each event has its OWN
  // inbox, whose address its owner obtains with `openDocumentInbox`.
  //
  // IDEMPOTENCE / CONVERGENCE: the count is DERIVED from the SET of distinct
  // active registrations (`materializeAttendance`: distinct join uids MINUS
  // cancelled ones), never an unbounded ±1. A broker re-sync replays the same
  // deposits → same set → same count. `participantCount = activeRegistrations.size`
  // (no host baseline — the creator does not auto-participate). The write is GUARDED
  // (write only when the value actually changes) so re-materializing an unchanged
  // inbox does not thrash the doc / loop the reactive read.
  //
  // OWNER-OFFLINE = EVENTUAL (brief §E.2): only the owner's session runs this, so
  // while the owner is disconnected the count doesn't advance for others (the
  // deposits persist in the inbox — nothing is lost; a future service will
  // materialize on the owner's behalf).
  // Dedup owned events by their CANONICAL id-form (base repo id, stripping any
  // `:v:<overlay>` suffix): the SAME event can enter `ownedEventIds` under two
  // overlays (create-time vs a later `listMyEntityDocs` backfill), and iterating
  // both would materialize + count-write the same event twice. Keep ONE real NURI
  // per canonical id as the write/anchor target (the count is written on a live
  // doc NURI — never a stripped id). See `canonicalEventId` in registration.ts.
  const ownedEvents = React.useMemo<Nuri[]>(() => {
    const byCanon = new Map<string, Nuri>();
    for (const nuri of ownedEventIds) {
      const c = canonicalEventId(nuri);
      if (!byCanon.has(c)) byCanon.set(c, nuri);
    }
    return [...byCanon.values()].sort();
  }, [ownedEventIds]);
  // Primitive identity of the set above — the effect's dependency (an array is a
  // new reference on every render).
  const ownedKey = ownedEvents.join('|');

  // WHICH EVENTS MAY THIS SESSION WRITE — the same owned set, asked the other way
  // round. Nothing new is called: the contract makes writing a document and owning
  // it the same thing, so `listMyEntityDocs('public')` (already resolved above for
  // the materializer) is the whole answer. Matched on the CANONICAL id-form, like
  // every other event-id comparison here, so an event reached under a different
  // overlay is still recognized as the same event.
  const getEventOwnership = useCallback((eventId: string): EventOwnership => {
    const canon = canonicalEventId(eventId);
    // A hit is authoritative EVEN BEFORE any listing has answered: an event this
    // session just created was claimed directly, and it is mine whatever a
    // listing later says. Checked FIRST, so ownership always beats a stale miss.
    if (ownedCanonicalIds.has(canon)) return 'mine';
    // A miss counts only for events a resolved listing actually looked past.
    if (ruledOutEventIds.has(canon)) return 'not-mine';
    // Otherwise the answer is genuinely still coming — a listing is pending, has
    // failed, or this event arrived after the last one. Never 'not-mine' here.
    return 'unknown';
  }, [ownedCanonicalIds, ruledOutEventIds]);
  // Last count WRITTEN per owned event (keyed by canonical id), stamped with the
  // cycle that wrote it. The stamp is what makes a STALE cycle harmless — see the
  // guard below.
  const materializedCountRef = useRef<Map<string, { count: number; seq: number }>>(new Map());
  // Monotonic cycle number, shared by every materialize cycle of this session. It
  // has to outlive the effect: when the owned set changes the effect re-runs, and
  // a cycle started by the PREVIOUS run can still be in flight.
  const cycleSeqRef = useRef(0);
  useEffect(() => {
    if (!ready) return;
    const owned = ownedEvents;
    if (owned.length === 0) return;
    let cancelled = false;

    // ONE inbox address per owned event, resolved ONCE here and shared by the two
    // things that must not disagree: the cycle that READS the inbox and the watch
    // that SUBSCRIBES to it. Resolving them apart is how the owner ends up watching
    // one address while a deposit lands in another — the sign-up is then never seen
    // in the session that made it. `openDocumentInbox` is itself single-flight per
    // document (utils/storeRegistry), so the app can no longer open a second inbox
    // at all; this map is the narrower promise that the reader and the watcher hold
    // the very same value.
    //
    // Resolved on first need and kept — but a FAILED resolution is released, not
    // kept: it means the address is UNKNOWN, so the next trigger must really ask
    // again instead of inheriting a permanent "no inbox".
    let resolvingInboxes: Promise<Map<Nuri, Nuri>> | null = null;
    const inboxesForOwnedEvents = (): Promise<Map<Nuri, Nuri>> => {
      if (resolvingInboxes) return resolvingInboxes;
      const attempt = (async () => {
        const byEvent = new Map<Nuri, Nuri>();
        for (const evId of owned) byEvent.set(evId, await openDocumentInbox(evId));
        return byEvent;
      })().catch(err => {
        if (resolvingInboxes === attempt) resolvingInboxes = null;
        throw err;
      });
      resolvingInboxes = attempt;
      return attempt;
    };

    const runCycle = async (trigger: string) => {
      if (cancelled) return;
      const seq = ++cycleSeqRef.current;
      try {
        const inboxes = await inboxesForOwnedEvents();
        console.log(
          `${logPrefix} owner participation materialize START (trigger=${trigger}, cycle=${seq}) — ` +
            `${owned.length} owned event(s)`,
        );
        const notifs: FpNotificationData[] = [];
        // Each event has its OWN inbox, and only its OWNER can open it. The address
        // comes from the shared map above — the same one the watch subscribed; a
        // depositor never sees it (they name the document instead).
        for (const [evId, targetInbox] of inboxes) {
          const canonId = canonicalEventId(evId);
          // BEFORE — the event's readable detail (short id + title) and the
          // participantCount value as currently READ/exposed (the app-side `events`
          // state), captured before this cycle's derive+write. Comparing this to the
          // AFTER log below tells whether the counter is a DATA problem (never
          // incremented) or a DISPLAY/read problem (incremented but not re-read).
          //
          // Read through the REF, never the closure: this effect only re-runs on
          // [ready, ownedKey], so the `events` it captured is the snapshot from the
          // render that wired the watch — which is why this line printed `(unknown)`
          // for the whole session and told the last investigation nothing. Matched
          // on the canonical id-form, like every other event-id comparison here.
          const knownEvent = eventsRef.current.find(e => canonicalEventId(e.id) === canonId);
          const knownCount = knownEvent?.participantCount;
          console.log(
            `${logPrefix} participation materialize — event=${canonId}` +
              (knownEvent?.title ? ` "${knownEvent.title}"` : '') +
              ` — participantCount before write (as currently read) = ${knownCount ?? '(unknown)'}`,
          );
          // (1) COUNT — derive the distinct active-registration set for this event
          // and write it on MY OWN event doc (only when it changed). `materializeAttendance`
          // reads through the synced-view contract (`inbox.readSynced`), so a
          // registrant's deposit is visible even on a cold session.
          const active = await materializeAttendance(targetInbox, evId);
          const nextCount = active.length; // no host baseline (creator not auto-in)
          const written = materializedCountRef.current.get(canonId);
          // THE VALUE A CYCLE CARRIES IS ONLY AS FRESH AS THE READ IT CAME FROM.
          // Cycles of this effect can no longer interleave (they are serialized
          // below), but the effect re-runs whenever the owned set changes, and a
          // cycle from the previous run can still be in flight — holding a count it
          // derived BEFORE the fresher one's. Writing it would put the stale value
          // back on the document, which is a count that goes backwards for no
          // visible reason. So a cycle may only overwrite what an OLDER cycle wrote.
          if (written && written.seq > seq) {
            console.log(
              `${logPrefix} owner participation materialize — event=${canonId}: cycle ${seq} is STALE ` +
                `(cycle ${written.seq} already wrote ${written.count}) — not writing ${nextCount}`,
            );
          } else if (written?.count === nextCount) {
            // Write ONLY when the derived value actually changes (anti-loop). This
            // memo does NOT lock in a premature 0: the barrier-gated read above makes
            // the first post-connection materialize see the real deposits, so once the
            // set becomes non-empty the value differs and the correct count is
            // written. A transient write failure reverts the memo so the next trigger
            // retries. The guard's sole job is to avoid re-writing an UNCHANGED value.
            console.log(
              `${logPrefix} owner participation materialize — event=${canonId}: ` +
                `participantCount unchanged (${nextCount}) — no write`,
            );
          } else if (!cancelled) {
            materializedCountRef.current.set(canonId, { count: nextCount, seq });
            console.log(
              `${logPrefix} owner participation materialize — event=${canonId}: ` +
                `participantCount ${written?.count ?? '(none)'} → ${nextCount} (writing own doc, cycle=${seq})`,
            );
            // The write lands on the owned event doc, which `watchShape('public')`
            // already subscribes → the reactive read re-renders the new count on
            // the broker push (no manual re-query).
            let writeOk = true;
            await updateEntityField(evId, evId, 'participantCount', int(nextCount))
              .catch(err => {
                // Revert the memo so a transient write failure retries next trigger
                // — but only if it is still OURS. A fresher cycle's value stands.
                writeOk = false;
                const current = materializedCountRef.current.get(canonId);
                if (current && current.seq === seq) materializedCountRef.current.delete(canonId);
                console.error(`${logPrefix} owner participation materialize count WRITE FAILED:`, err);
              });
            if (writeOk) {
              // AFTER — the write has landed on the owner's own doc. N → M reuses the
              // SAME "before" reference point logged above, so it is directly
              // comparable: a DISPLAY-read log (see `selectedEvent`, above in this
              // file) still showing the old N after this fires means the counter data
              // is fine and it is the read side that lags.
              console.log(
                `${logPrefix} participation materialize — event=${canonId}: ` +
                  `participantCount AFTER write = ${knownCount ?? '(unknown)'} → ${nextCount}`,
              );
            }
          }
          // (2) NOTIFICATIONS — surface "new participant" deposits (unchanged T02.c).
          const evNotifs = await readRegistrationNotifications(targetInbox, evId);
          notifs.push(...evNotifs);
        }
        if (!cancelled && notifs.length) {
          setNotifications(prev => {
            const seen = new Set(prev.map(n => n.id));
            const merged = [...prev];
            for (const n of notifs) if (!seen.has(n.id)) { seen.add(n.id); merged.push(n); }
            return merged;
          });
        }
      } catch (err) {
        console.error(`${logPrefix} owner participation materialization failed:`, err);
      }
    };

    // A CYCLE IS A READ-DERIVE-WRITE, AND TWO OF THEM MUST NOT OVERLAP. The two
    // triggers below fire within the same instant on the connection that creates an
    // event, and run concurrently they both read the inbox before either writes —
    // so the one that finishes last puts its own, older reading back on the
    // document. Serialized, a request arriving mid-cycle is served by ONE follow-up
    // cycle once the current one has finished (a cycle re-derives everything from
    // the inbox, so one follow-up covers however many requests it coalesces).
    const materialize = createSerialTask(runCycle);

    // (A) RELIABLE-AT-CONNECTION: run one materialization directly on this trigger
    // ([ready, ownedKey]). This is the spec's core — the owner, at its NEXT
    // CONNECTION, deterministically processes its owned events' inbox, reading
    // through the synced-view contract. It does NOT depend on a cross-session
    // inbox push arriving.
    void materialize('connection').catch(err =>
      console.error(`${logPrefix} owner participation materialize cycle rejected:`, err));

    // (B) SAME-SESSION LIVE: `inbox.watch` fires on the initial state push and on
    // every later deposit visible to THIS verifier (a local deposit, or a remote one
    // that pushes) — no polling. Re-materialize on each so the owner's own session
    // stays live when a deposit does push. Cross-session convergence does NOT rely on
    // this (it relies on (A) at the owner's next connection); this only sharpens the
    // same-session/live case. One watch PER owned event — each event has its OWN
    // inbox document — and the address watched is the one taken from the SHARED map
    // above, so what is watched is exactly what the cycle reads.
    const unsubscribes: Array<() => void> = [];
    void (async () => {
      const inboxes = await inboxesForOwnedEvents();
      if (cancelled) return;
      for (const targetInbox of inboxes.values()) {
        unsubscribes.push(inbox.watch(targetInbox, () =>
          void materialize('inbox-push').catch(err =>
            console.error(`${logPrefix} owner participation materialize cycle rejected:`, err))));
      }
    })().catch(err => console.error(`${logPrefix} owner inbox watch wiring failed:`, err));
    return () => { cancelled = true; for (const stop of unsubscribes) stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, ownedKey]);

  // Protected-sharing act: the app owns the relationship concept — it declares the
  // current identity's own connections (a Festipod domain fact) and, for each
  // bilateral link, hands the SDK directed read grants so an owner's connections
  // may read that owner's PROTECTED entities (public = all; private = owner only).
  // The declaration names only the current user's own peers, asserted as the
  // current identity. The app carries no access CHECK (see knowledge_trust-model)
  // — it only declares its own relationship graph, then trusts the SDK to enforce
  // the resulting per-document grants. No store id, no document NURI crosses here.
  useEffect(() => {
    if (!ready || !currentUserId) return;
    // `inbox.share(doc, toUser)` names a PERSON, so the app models both sides of
    // a relationship in ONE key space: the normalized profile username. It maps
    // each peer IRI → that key before declaring, and asserts AS its own key.
    // Peers with no known profile are skipped (they cannot be named).
    const idKeyOf = (userIri: string): string | undefined => {
      const u = users.find(x => x.id === userIri);
      return u?.username ? normalizeIdentifier(u.username) : undefined;
    };
    const selfKey = idKeyOf(currentUserId);
    if (!selfKey) return;
    const myPeers = friendships
      .filter(f => f.userId === currentUserId || f.friendId === currentUserId)
      .map(f => (f.userId === currentUserId ? f.friendId : f.userId))
      .map(idKeyOf)
      .filter((k): k is string => !!k);
    // Sharing is I/O now (a key handed to each neighbour's inbox), so it is
    // fire-and-forget from this effect — nothing downstream waits on it.
    void declareConnections(myPeers, selfKey)
      .catch(err => console.error(`${logPrefix} declareConnections failed:`, err));
  }, [ready, friendships, currentUserId, users]);

  const queries = buildQueries(
    events, users, participations, meetingPoints, friendships, currentUserId,
  );

  console.log(`${logPrefix} Render — NG | events:`, events.length,
    '| users:', users.length, '| participations:', participations.length,
    '| selectedEvent:', selectedEvent?.title ?? '(none)');

  // --- Mutations (NG) ---
  // Each entity is written as its OWN document, created via the SDK in its scope
  // (`createEntityDoc(scope)`) — never a store-level document. Creating a doc
  // appends its NURI to the scope index, which `watchShape` subscribes; the new
  // entity enters the reactive read on the resulting push (no manual registration).
  // The SDK declares the per-document ReadCap policy on create (public / protected /
  // private) — the app carries no access logic.

  const createEvent = useCallback(async (event: Omit<FpEventData, 'id'>): Promise<FpEventData> => {
    console.log(`${logPrefix} createEvent (NG):`, event.title);
    // The SDK create returns THIS entity's OWN public document and declares its
    // access policy (public → world-readable). Placement is named by SCOPE
    // alone: the session belongs to one user, so there is no owner to name.
    // A creation that cannot be recorded THROWS — it never hands back a
    // reference that would read empty forever — so this rejects the whole
    // `createEvent` rather than returning a half-made event.
    // Create the event's OWN document in the PUBLIC scope (one doc per entity),
    // then WRITE the event RDF DIRECTLY into that document (writeEntity) — not via
    // the scope-coupled `ngSet.add`, which can't write into a not-yet-subscribed
    // per-entity doc against the real broker. The doc's NURI is appended to the
    // public scope index, which `watchShape('public')` subscribes → the event
    // enters the reactive read on the push. The written subject IRI is the `@id`.
    const eventGraph = await createEntityDoc('public');
    // An event is a document people SIGN UP TO, so its owner opens its inbox
    // here, at creation. A document only HAS one if its owner opened it; without
    // this, a registrant's deposit would have nothing to reach.
    await openDocumentInbox(eventGraph);
    const eventId = await writeEntity(eventGraph, ENTITY_TYPE.event, {
      title: str(event.title), description: str(event.description), date: str(event.date),
      location: str(event.location), distance: flt(event.distance),
      // No host notion: the creator merely SIGNALS a public event and is NOT
      // obliged to participate, so the count starts at 0 (the owner-materializer
      // derives it from the active-registration set — |active|, no host baseline).
      participantCount: int(event.participantCount || 0),
      coverImage: str(event.coverImage),
    });
    // OPTION B: this event's doc is MINE (I just created it), so track it as owned
    // → the owner-materializer subscribes to its inbox and maintains its count.
    setOwnedEventIds(prev => (prev.includes(eventGraph) ? prev : [...prev, eventGraph]));
    // OPTIMISTIC OVERLAY: surface the created event IMMEDIATELY (its id = eventId =
    // the doc NURI, the SAME id `watchShape('public')` will surface as `s.subject`,
    // so the reconciliation effect drops this add once the push arrives). Without
    // it, `getEvent(eventId)` right after create returns undefined until the push.
    // participantCount starts at 0 (creator does not auto-participate).
    const optimisticEvent: FpEventData = { ...event, id: eventId, participantCount: event.participantCount || 0 };
    setPendingAddEvents(prev => (prev.some(e => e.id === eventId) ? prev : [...prev, optimisticEvent]));
    // The creator does NOT auto-participate (no host notion — settled product
    // decision): NO participation is written on create. The creator sees "J'y
    // serai" and may join/leave their own event like anyone else.
    if (currentUserId) setSelectedEventId(eventId);
    const addedEvent = { "@id": eventId, title: event.title };
    // Make the PUBLIC event discoverable: submit its reference to the SDK global
    // discovery index (an SDK act — the app holds no index/store id). The SDK
    // enforces public-only: passing the event's own document lets it refuse a
    // non-public doc. Best-effort: a failed submission must not roll back a
    // successful event creation.
    if (addedEvent) {
      // NO index submission any more. There is no discovery: a reader reaches a
      // document only by FOLLOWING A LINK it was given. Announcing a public event
      // beyond its creator is therefore a Festipod-level concern (a directory
      // document whose link the app knows), not an SDK call — see the directory
      // work. Until it lands, a created event is reachable by its creator only.
    }
    return { ...event, id: addedEvent?.["@id"] || `ng-pending-${Date.now()}` };
  }, [currentUserId]);

  const updateEvent = useCallback(async (id: string, updates: Partial<FpEventData>) => {
    console.log(`${logPrefix} updateEvent (NG):`, id, updates);
    // The event's `@id` IS its own document NURI (one entity = one document), so
    // it is both the write graph and the subject. Persist each provided mutable
    // field DIRECTLY via SPARQL (the durable write); `watchShape` re-reads on the
    // resulting broker push (the doc is already subscribed) — no manual re-query.
    // No pre-flight NURI guard: every SDK entry takes the reference as it stands
    // and validates at its own door.
    const graph = id;
    const persists: Promise<void>[] = [];
    if (updates.participantCount !== undefined) {
      persists.push(updateEntityField(graph, id, 'participantCount', int(updates.participantCount)));
    }
    if (updates.title !== undefined) persists.push(updateEntityField(graph, id, 'title', str(updates.title)));
    if (updates.description !== undefined) persists.push(updateEntityField(graph, id, 'description', str(updates.description)));
    if (updates.date !== undefined) persists.push(updateEntityField(graph, id, 'date', str(updates.date)));
    if (updates.location !== undefined) persists.push(updateEntityField(graph, id, 'location', str(updates.location)));
    if (updates.distance !== undefined) persists.push(updateEntityField(graph, id, 'distance', flt(updates.distance)));
    await Promise.all(persists).catch(err => console.error(`${logPrefix} persist event update failed:`, err));
  }, []);

  const joinEvent = useCallback(async (eventId: string, userId?: string) => {
    const uid = userId || currentUserId;
    console.log(`${logPrefix} joinEvent (NG):`, eventId, 'user:', uid);
    // A Participation MUST carry a user reference (SHEX `fp:user` is mandatory) —
    // writing one without it produces an entity the read drops (the participation
    // silently never round-trips). REJECT rather than return: returning here made
    // the sign-up a no-op that wrote nothing, threw nothing and let the screen
    // congratulate the user. The cause is named, because it is actionable: no
    // profile of mine is resolved yet.
    if (!uid) {
      const msg =
        `joinEvent refused for event=${canonicalEventId(eventId)}: no profile of mine is resolved, ` +
        `so a Participation would carry no fp:user and would never round-trip. ` +
        `Wait for the profile to be created/read, or resolve the ambiguity reported above.`;
      console.error(`${logPrefix} ${msg}`);
      throw new Error(msg);
    }
    // IDEMPOTENCE — check AUTHORITATIVELY against the broker, not the reactive set.
    // The reactive participation set can lag a just-written participation, so a
    // second join checking only the set would write a DUPLICATE (breaking "exactly
    // one participation"). The broker query sees the real state regardless of lag.
    //
    // A FAILED count is UNKNOWN, not zero, so it is NOT caught here: reading it as
    // "not participating yet" is precisely how a duplicate gets written. The
    // rejection propagates out of `joinEvent`, and the screen that called it says
    // the sign-up could not be recorded — which is the truth.
    const already = await countUserParticipations(eventId, uid);
    if (already > 0) {
      console.log(`${logPrefix} Already participating (broker-confirmed), skipping`);
      return;
    }
    // 1) Persist the Participation as its OWN document in the PROTECTED scope
    //    (one doc per entity). Its NURI is appended to the protected scope index,
    //    which `watchShape('protected')` subscribes → the participation enters
    //    the reactive read on the push.
    //    A creation that cannot be recorded THROWS, so a failure here rejects
    //    `joinEvent` instead of depositing a registration for a participation
    //    document that would read empty forever.
    const partGraph = await createEntityDoc('protected');
    // WRITE the participation RDF DIRECTLY into its own document (writeEntity) —
    // not via the scope-coupled `ngSet.add` (can't write a not-yet-subscribed
    // per-entity doc against the real broker). The written subject is the
    // participation's `@id` (its own graph is partGraph, used later by the
    // authoritative delete).
    await writeEntity(partGraph, ENTITY_TYPE.participation, {
      event: iri(eventId), user: iri(uid), isConfirmed: bool(true),
    });
    // OPTIMISTIC OVERLAY: surface the participation IMMEDIATELY so the joiner shows
    // up in the list and `isParticipating` is true before the broker push. Its id =
    // partGraph = the doc NURI, the SAME id `watchShape('protected')` will surface
    // as `s.subject` (adaptParticipations sets `id: s.subject`) → the reconciliation
    // effect drops this add once the push arrives. If a stale pendingRemove targeted
    // this exact id (re-join same doc — never happens, ids are fresh), clear it too.
    const optimisticPart: FpParticipationData = { id: partGraph, eventId, userId: uid, isConfirmed: true };
    setPendingAddParticipations(prev => (prev.some(p => p.id === partGraph) ? prev : [...prev, optimisticPart]));
    // OPTION B (brief §B): the joiner does NOT write `participantCount` on the
    // EVENT doc — that doc belongs to the OWNER, and a non-owner write there is an
    // isolation violation (NextGraph write is membership-bound; there is no append
    // — see brief §1). The count now moves only via the OWNER materializing this
    // deposit onto its OWN event doc (the `hostedEventIds` materializer below).
    // The joiner's sole writes are: their OWN participation doc (above) + the
    // inbox DEPOSIT (below). While the owner is offline the count doesn't advance
    // for others — accepted eventual behaviour (brief §E.2); nothing is lost.
    // 2) Tell the host. THE DEPOSIT IS THE DELIVERY: `inbox.postToDocument(doc, …)`
    //    is what the surface publishes for reaching a document's owner — anyone
    //    may deposit, only the owner reads. The host's notification is then built
    //    on the OWNER's side, out of the deposits it reads from its own event's
    //    inbox (`readRegistrationNotifications`, in the materializer above).
    //
    //    Nothing else is written here. A host FpNotification used to be minted at
    //    this point into the JOINER's own protected scope with `recipient` set to
    //    the event — a document the host cannot read, and never will: the joiner
    //    owns it and the surface has no way to hand it over. It also pushed that
    //    notification into THIS session's own list, so the joiner saw a
    //    "new participant" notice addressed to someone else. Both are gone; the
    //    deposit alone carries the news, and it reaches its reader.
    //
    //    Best-effort: a failed deposit must not roll back a successful
    //    registration (the participation document is already written).
    try {
      const registrantId = uid || null; // no current user → anonymous deposit
      // The event's `@id` IS its document NURI, and a deposit NAMES that document
      // — the joiner resolves no inbox and holds no address.
      // Carry the joiner's participation-doc NURI so the owner (if a connection)
      // could read it in clear; the count itself does not depend on reading it.
      console.log(
        `${logPrefix} joinEvent — depositing participation registration into event inbox: ` +
          `event=${canonicalEventId(eventId)} user=${uid} (count now moves via the OWNER ` +
          `materializing this deposit on its own doc, at its next connection)`,
      );
      const { uid: depositUid } = await depositRegistration(eventId, registrantId, partGraph);
      // Remember the join uid so a same-session leave can cancel it precisely.
      joinUidsRef.current.set(`${eventId}|${uid}`, depositUid);
    } catch (err) {
      console.error(`${logPrefix} joinEvent inbox deposit failed:`, err);
    }
  }, [events, currentUserId]);

  const leaveEvent = useCallback(async (eventId: string, userId?: string) => {
    const uid = userId || currentUserId;
    console.log(`${logPrefix} leaveEvent (NG):`, eventId, 'user:', uid);
    // Find the participation in the union-read set. Each participation is its OWN
    // document (writeEntity uses the doc NURI as the subject), so `part.id` is BOTH
    // the subject IRI AND the graph NURI it lives in.
    const part = participations.find(p => p.eventId === eventId && p.userId === uid);
    // REJECT rather than pretend. Withdrawal is AUTHORITATIVE (see
    // caveat_participation-deletion): the caller only reaches here because it
    // believes a participation exists, so finding none is a real disagreement
    // about the state — either no profile of mine is resolved (`uid` empty), or
    // the participation the screen showed is not in the set. Returning silently
    // deleted nothing while the screen announced a withdrawal, and the sign-up
    // came back on the next read.
    if (!part) {
      const msg = uid
        ? `leaveEvent refused for event=${canonicalEventId(eventId)}: no participation of user=${uid} ` +
          `in the participation set — nothing was deleted, so the withdrawal must not be announced.`
        : `leaveEvent refused for event=${canonicalEventId(eventId)}: no profile of mine is resolved, ` +
          `so the participation to withdraw cannot even be named.`;
      console.error(`${logPrefix} ${msg}`);
      throw new Error(msg);
    }
    // DÉSINSCRIPTION FIX (caveat_participation-deletion): the AUTHORITATIVE deletion
    // is a SPARQL DELETE via the real injected `ng` (docs.sparqlUpdate), which
    // removes the Participation server-side so it does NOT resurrect after re-sync.
    // The delete targets the participation's own document (part.id) and is
    // identified by its OWN subject IRI (part.id), not a string-match on object IRIs.
    const graphNuri = part.id;
    const subjectIri = part.id;
    let result;
    try {
      result = await deleteParticipation(graphNuri, eventId, uid, subjectIri);
    } catch (err) {
      console.error(`${logPrefix} SPARQL DELETE participation failed:`, err);
      throw err instanceof Error ? err : new Error(String(err));
    }
    // AUTHORITATIVE verification: only proceed once the broker RE-QUERY confirms
    // the participation is gone (remaining === 0). If the delete matched nothing,
    // surface it rather than falsely flip the UI (it would resurrect on re-sync).
    if (result.remaining > 0) {
      const msg = `${logPrefix} leaveEvent: SPARQL delete removed nothing ` +
        `(before=${result.before}, remaining=${result.remaining}, bySubject=${result.bySubject}) ` +
        `for event=${eventId} user=${uid} — NOT flipping UI (would resurrect).`;
      console.error(msg);
      throw new Error(msg);
    }
    // OPTIMISTIC OVERLAY: the delete is broker-confirmed (remaining === 0) but the
    // reactive `watchShape('protected')` set only drops the participation on the
    // NEXT push (a beat later). Mark its id removed NOW so the leaver disappears
    // from the list immediately; the reconciliation effect clears this pendingRemove
    // once the reactive set no longer carries the id. Also drop it from
    // pendingAddParticipations in case it was still only-optimistic (join+leave in
    // the same session before the join's push landed).
    setPendingRemoveIds(prev => {
      if (prev.has(part.id)) return prev;
      const next = new Set(prev);
      next.add(part.id);
      return next;
    });
    setPendingAddParticipations(prev => {
      const next = prev.filter(p => p.id !== part.id);
      return next.length === prev.length ? prev : next;
    });
    // OPTION B, symmetric (brief §B "Désinscription"): the leaver does NOT write
    // `participantCount` on the EVENT doc (owner-owned — same isolation violation
    // as the join). Instead it DEPOSITS a `leave-participant` marker into the
    // event inbox; the OWNER materializes it and recomputes the count on its OWN
    // doc (idempotent — a re-synced leave never double-decrements, since the count
    // is derived from the SET of distinct active registrations, not from −1).
    try {
      const registrantId = uid || null;
      // Same as `joinEvent`: the leave marker NAMES the event document; no inbox
      // address is resolved on the depositor's side.
      // Carry the join uid when this session minted it (precise cancellation);
      // otherwise the owner falls back to (eventId, userId) matching.
      const regUid = joinUidsRef.current.get(`${eventId}|${uid}`);
      console.log(
        `${logPrefix} leaveEvent — depositing participation leave marker into event inbox: ` +
          `event=${canonicalEventId(eventId)} user=${uid} regUid=${regUid ?? '(none)'}`,
      );
      await depositLeave(eventId, registrantId, regUid);
      joinUidsRef.current.delete(`${eventId}|${uid}`);
    } catch (err) {
      console.error(`${logPrefix} leaveEvent inbox deposit failed:`, err);
    }
    // The participation doc is subscribed by `watchShape('protected')`; the SPARQL
    // DELETE pushes → the reactive read drops it (`isParticipating` reflects it).
    // The count itself follows the owner's materialization of the leave marker
    // (reactive, cross-session).
  }, [participations, events, currentUserId]);

  const addMeetingPoint = useCallback((mp: Omit<FpMeetingPointData, 'id'>) => {
    setMeetingPoints(prev => [...prev, { ...mp, id: `ng-mp-${Date.now()}` }]);
  }, []);

  const addFriend = useCallback((friendId: string) => {
    setFriendships(prev => {
      if (prev.some(f =>
        (f.userId === currentUserId && f.friendId === friendId) ||
        (f.userId === friendId && f.friendId === currentUserId)
      )) return prev;
      return [...prev, { id: `ng-fr-${Date.now()}`, userId: currentUserId, friendId }];
    });
  }, [currentUserId]);

  const updateProfile = useCallback(async (updates: Partial<FpUserData>) => {
    console.log(`${logPrefix} updateProfile (NG):`, updates);
    // MY profile is its own document (subject IRI = doc NURI), and it is the ONLY
    // document these edits may land in. There used to be a `?? users[0]` fallback
    // here: with no profile resolved, a person editing their own pseudo wrote it
    // into a stranger's profile document. Refuse instead — and say which of the
    // two reasons it is, since only one of them clears up on its own.
    const target = currentUser;
    if (!target) {
      const msg =
        `updateProfile refused: no profile of mine is resolved, and these edits must never land in ` +
        `someone else's profile document.`;
      console.error(`${logPrefix} ${msg}`);
      throw new Error(msg);
    }
    const graph = target.id;
    const persists: Promise<void>[] = [];
    if (updates.name !== undefined) persists.push(updateEntityField(graph, graph, 'name', str(updates.name)));
    if (updates.initials !== undefined) persists.push(updateEntityField(graph, graph, 'initials', str(updates.initials)));
    if (updates.username !== undefined) persists.push(updateEntityField(graph, graph, 'username', str(updates.username)));
    if (updates.role !== undefined) persists.push(updateEntityField(graph, graph, 'role', str(updates.role)));
    if (updates.isPublic !== undefined) persists.push(updateEntityField(graph, graph, 'isPublic', bool(updates.isPublic)));
    await Promise.all(persists).catch(err => console.error(`${logPrefix} persist profile update failed:`, err));
  }, [currentUser, users]);

  const loadTestData = useCallback(async (): Promise<BootstrapResult> => {
    console.log(`${logPrefix} loadTestData (NG)`);
    // THIS NO LONGER SEEDS. `bootstrapWallet` refuses while the fixture seed is
    // switched off (`fixtureSeedEnabled`), so what comes back is `seeded: false`
    // with no documents, and everything below is a no-op on that empty result.
    // The call is kept, and kept honest, rather than removed: the switch is what
    // decides, in one place, and flipping it back restores this path unchanged.
    // An EXPLICIT load is authoritative — SUPPRESS the dev auto-seed so only ONE
    // seed runs (marking the guard at the START, before the awaited seed, closes
    // the window where the auto-seed effect could also fire on a still-empty read).
    hasTriedAutoSeed.current = true;
    // Same reading as the auto-seed gate: my own profile is not "data in the
    // wallet" — it exists on every wallet from the first sign-in, so counting it
    // would make an explicit load a permanent no-op.
    const walletHasData = events.length > 0 || users.some(u => u.id !== myProfileDocId);
    const result = await bootstrapWallet(walletHasData, createEntityDoc);
    // The seeded per-entity docs are appended to their scope indices, which
    // `watchShape` subscribes → they enter the reactive reads on the push. No
    // manual registration / re-list.
    // OWNERSHIP is a different question from READING, and it is not answered by
    // the push: these public event docs were created by THIS session, so claim
    // them — otherwise the owner-materializer never opens their inboxes and their
    // `participantCount` is never derived.
    claimOwnedEventDocs(result.createdDocs.public);
    // The seeded PROTECTED docs are deliberately NOT claimed as mine: they are
    // FIXTURES (nobody signed in as them), and folding them into the owned-profile
    // set is exactly what would make "which profile is mine" ambiguous.
    return result;
  }, [events.length, users, myProfileDocId, claimOwnedEventDocs]);

  return {
    currentUserId, currentUser, currentPrincipal,
    events, users,
    participations,
    meetingPoints,
    friendships,
    notifications,
    selectedEventId, setSelectedEventId, selectedEvent,
    selectedUserId, setSelectedUserId, selectedUser,
    ...queries,
    getEventOwnership,
    createEvent, updateEvent, joinEvent, leaveEvent,
    addMeetingPoint, addFriend, updateProfile, loadTestData,
  };
}

// ============================================================================
// Provider — switches between local and NG
// ============================================================================

export function LocalDataProvider({ children, empty }: { children: ReactNode; empty?: boolean }) {
  const data = useLocalData(empty);
  return <FestipodDataContext.Provider value={data}>{children}</FestipodDataContext.Provider>;
}

function NgDataProvider({ children }: { children: ReactNode }) {
  const data = useNgData();
  return <FestipodDataContext.Provider value={data}>{children}</FestipodDataContext.Provider>;
}

export function FestipodDataProvider({ children }: { children: ReactNode }) {
  const { status } = useNextGraph();
  // SIGNING IN MUST HAVE SETTLED BEFORE THE DATA PROVIDER STARTS. Every call the
  // NG provider makes addresses "MY documents" — placement by scope
  // (`createEntityDoc` / `listMyEntityDocs`) and the reactive scope reads alike —
  // and there is no "my" until `ensureIdentity()` has answered. Being CONNECTED is
  // not being SIGNED IN: the session opens first, the identity settles after. A
  // provider mounted in between fires its reads and its owned-documents
  // resolution against no identity — they fail once, at mount, and the memoized
  // observables never retry, so the screens stay empty for the whole session
  // while the writes that come later succeed.
  //
  // The order is carried HERE, by which provider is mounted, rather than by a
  // guard repeated in every effect — the invariant cannot then be forgotten by
  // the next call site.
  const signedIn = useCurrentPrincipal() !== '';
  console.log('[app][data] Provider — NG status:', status, '| signed in:', signedIn);

  if (status === 'connected' && signedIn) {
    return <NgDataProvider>{children}</NgDataProvider>;
  }
  if (status === 'connecting' || status === 'connected') {
    // NG initializing, or connected but not signed in yet: show empty state (no
    // misleading seed data flash, and no read issued before there is a "my").
    return <LocalDataProvider empty>{children}</LocalDataProvider>;
  }
  // Disconnected or error: demo mode with seed data
  return <LocalDataProvider>{children}</LocalDataProvider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useFestipodData() {
  const context = useContext(FestipodDataContext);
  if (!context) {
    throw new Error('useFestipodData must be used within a FestipodDataProvider');
  }
  return context;
}
